import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Paper
} from '@mui/material'
import {
  Store as StoreIcon,
  CheckCircle as CheckCircleIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material'
import { apiClient } from '../../services/api'
import { login } from '../../services/auth'
import { poolwerxColors } from '../../theme/colors'
import StandaloneCardLayout from '../../components/layouts/StandaloneCardLayout'
import { googleSignInButtonSx } from '../../components/auth/authButtonStyles'
import { GoogleIcon } from '../../components/SystemIcons'
import { Helmet } from 'react-helmet-async'
import DevEnvironmentBanner from '../../components/DevEnvironmentBanner'

interface InvitationInfo {
  token: string
  fp_code: string
  fp_name: string
  franchise_name: string
  city: string
  state: string
  country: string
  is_valid: boolean
  message?: string
}

const InvitationPage: React.FC = () => {
  const { fp_code, token } = useParams<{ fp_code: string; token: string }>()
  const navigate = useNavigate()
  const [invitation, setInvitation] = useState<InvitationInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [accepting, setAccepting] = useState(false)

  useEffect(() => {
    if (fp_code && token) {
      validateInvitation(fp_code, token)
    } else {
      setError('Invalid invitation link')
      setLoading(false)
    }
  }, [fp_code, token])

  const validateInvitation = async (fpCode: string, inviteToken: string) => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiClient.get<InvitationInfo>(`/invitations/${fpCode}/${inviteToken}`)
      setInvitation(response.data)
      
      if (!response.data.is_valid) {
        setError(response.data.message || 'This invitation is no longer valid')
      }
    } catch (err: any) {
      console.error('Failed to validate invitation:', err)
      // Handle validation error arrays from FastAPI
      let errorMessage = 'Invalid or expired invitation'
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          // Extract message from validation error array
          errorMessage = err.response.data.detail[0]?.msg || errorMessage
        } else if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail
        }
      }
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleAcceptInvitation = () => {
    if (!fp_code || !token) return
    
    // Start OAuth login - backend callback handles invitation processing via session data.
    // Fallback to /choose-franchise if session is lost (auto-redirects for single-FP users).
    const redirectPath = `/choose-franchise`
    login(redirectPath)
  }

  if (loading) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          backgroundImage: 'linear-gradient(135deg, #f5f5f5 0%, #e8f4f8 100%)',
        }}
      >
        <CircularProgress size={60} sx={{ color: poolwerxColors.teal }} />
        <DevEnvironmentBanner />
      </Box>
    )
  }

  // Prepare franchise details for the header
  const franchiseDetails = invitation && invitation.is_valid ? {
    name: invitation.franchise_name || invitation.fp_name,
    location: [invitation.city, invitation.state, invitation.country]
      .filter(Boolean)
      .join(', ')
  } : undefined

  // Generate page title
  const pageTitle = invitation?.fp_name 
    ? `Invitation - ${invitation.fp_name} | Inspector Tool`
    : 'Invitation | Inspector Tool'

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <StandaloneCardLayout 
        headerTitle="Invitation"
        headerIcon={invitation?.is_valid ? <StoreIcon sx={{ fontSize: 48 }} /> : undefined}
        franchiseDetails={franchiseDetails}
      >
        <Stack spacing={3}>
        {error ? (
          <>
            <Alert severity="error">
              {error}
            </Alert>
            <Button
              component={RouterLink}
              to="/login"
              variant="outlined"
              fullWidth
            >
              Return to Login
            </Button>
          </>
        ) : invitation && invitation.is_valid ? (
          <>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom sx={{ color: poolwerxColors.teal }}>
                Welcome to Inspector Tool
              </Typography>
              <Typography variant="body1" color="textSecondary" paragraph>
                You've been invited to access the Poolwerx Inspector Tool for this Franchise Partner.
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Connect your Google account to get started.
              </Typography>
            </Box>

            <Paper
              elevation={0}
              sx={{
                backgroundColor: '#f0f7ff',
                padding: 2,
                borderRadius: 1
              }}
            >
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                  <Typography variant="body2">
                    Access detailed exception reports
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                  <Typography variant="body2">
                    Use platform tools to make managing sales and inventory easier
                  </Typography>
                </Stack>
              </Stack>
            </Paper>

            <Button
              variant="outlined"
              fullWidth
              onClick={handleAcceptInvitation}
              disabled={accepting}
              startIcon={<GoogleIcon />}
              sx={googleSignInButtonSx}
            >
              {accepting ? 'Connecting...' : 'Connect with Google Account'}
            </Button>

            <Typography variant="caption" color="textSecondary" sx={{ textAlign: 'center' }}>
              By connecting, you agree to use your authorized Google account for authentication.
            </Typography>
          </>
        ) : (
          <Alert severity="error">
            Unable to process invitation. Please contact support if you believe this is an error.
          </Alert>
        )}
      </Stack>
    </StandaloneCardLayout>
    <DevEnvironmentBanner />
    </>
  )
}

export default InvitationPage